/**
 * AI analysis service: the system report assessment, a single component
 * assessment, the prediction review and the batch component assessment.
 * Port of ai-analysis-service.js with its promise defect fixed: the
 * critical and warning predictions are awaited before the prompt is built
 * (the original concatenated two Promises, threw, and always sent none).
 * Results are cached for five minutes; the transport, element, prediction
 * engine, inventory, alerts and time are injected.
 */
import type { Element, Subsystem } from '../../types';
import type { InventoryManager, PartSummary } from '../inventory';
import type { PredictionEngine, PredictionReport, SensorPrediction } from '../predictionEngine';
import { buildAiMessages, type AiTransport } from './transport';

export const AI_ANALYSIS_SYSTEM_PROMPT = 'You are a ground station systems analyst AI. Respond only with valid JSON.';
export const AI_ANALYSIS_CACHE_VALIDITY_MS = 5 * 60 * 1000;

export type AnalysisType = 'system_report' | 'component' | 'predictions';

export interface SystemReportAnalysis {
  overallAssessment: string;
  riskLevel: string;
  criticalIssues: string[];
  warningIssues: string[];
  recommendations: string[];
  maintenanceNeeds: { immediate: string[]; shortTerm: string[]; scheduled: string[] };
  inventoryActions: string[];
  predictedDowntime: string;
  confidenceLevel: number | string;
  _analysisType?: AnalysisType;
  _generatedAt?: string;
  [key: string]: unknown;
}

export interface ComponentAnalysis {
  healthScore: number;
  status: string;
  assessment: string;
  concerns: string[];
  recommendations: string[];
  predictedLifespan: string;
  maintenanceAdvice: string;
  _analysisType?: AnalysisType;
  _generatedAt?: string;
  [key: string]: unknown;
}

export interface PredictionsAnalysis {
  analysisConfidence: number;
  overallRisk: string;
  criticalComponents: unknown[];
  maintenanceSchedule: unknown[];
  procurementRecommendations: string[];
  riskMitigation: string[];
  confidenceNotes: string;
  _analysisType?: AnalysisType;
  _generatedAt?: string;
  [key: string]: unknown;
}

export type AnalysisResult = SystemReportAnalysis | ComponentAnalysis | PredictionsAnalysis;

export interface ActiveAlertSummary {
  type: string;
  subsystemName?: string;
  subsystemId?: string;
  message: string;
}

export interface SystemComponentSummary {
  id: string;
  name: string;
  description: string;
  status: 'OPERATIONAL' | 'WARNING' | 'CRITICAL';
  health: number;
  readings: { id: string; name: string; value: unknown; status: string; description: string; normalRange?: unknown }[];
}

export interface SystemData {
  element: { id: string; name: string | undefined; operationalValue: string | undefined; operationalStatus: string | undefined } | null;
  components: SystemComponentSummary[];
  alerts: ActiveAlertSummary[];
  predictions: SensorPrediction[];
  inventory: PartSummary[];
  readings: { total: number; healthy: number; warning: number; critical: number };
}

export interface AssessmentComponent {
  id: string;
  name: string;
  status?: string;
  health?: number;
  data?: { label?: string; value?: unknown; status?: string }[];
}

export interface ComponentAssessment {
  componentId: string;
  aiAssessment: string;
  riskLevel: string;
  confidence: number;
  action: string;
}

export interface AiAnalysisDeps {
  transport: AiTransport;
  nowMs: () => number;
  predictionEngine?: Pick<PredictionEngine, 'generateFullReport' | 'getCriticalPredictions' | 'getWarningPredictions'> | null;
  inventory?: Pick<InventoryManager, 'getAllParts' | 'getLowStockParts'> | null;
  /** The original always sent none (AlertSystem.getActive never existed). */
  getActiveAlerts?: () => ActiveAlertSummary[];
}

export interface AiAnalysisService {
  analyzeSystemReport(element: Element | null): Promise<SystemReportAnalysis>;
  analyzeComponent(element: Element | null, componentId: string): Promise<ComponentAnalysis>;
  analyzePredictions(): Promise<PredictionsAnalysis>;
  assessComponentsForReport(components: AssessmentComponent[]): Promise<ComponentAssessment[]>;
  collectSystemData(element: Element | null): Promise<SystemData>;
  clearCache(): void;
}

/** Every subsystem flattened: status WARNING on a yellow reading, CRITICAL on a red; health = mean of 100/60/20 per reading. */
export function collectComponentsRecursive(
  subsystems: Subsystem[] | undefined,
  components: SystemComponentSummary[],
  readings: SystemData['readings'],
): void {
  if (!subsystems) return;
  for (const sub of subsystems) {
    const component: SystemComponentSummary = {
      id: sub.id,
      name: sub.label || sub.id,
      description: sub.description || '',
      status: 'OPERATIONAL',
      health: 100,
      readings: [],
    };
    if (sub.data) {
      for (const d of sub.data) {
        readings.total++;
        const reading: SystemComponentSummary['readings'][number] = {
          id: d.id,
          name: d.label || d.id,
          value: d.value,
          status: d.status || 'green',
          description: d.description || '',
        };
        if (d.normalRange) reading.normalRange = d.normalRange;
        component.readings.push(reading);
        if (d.status === 'green') readings.healthy++;
        else if (d.status === 'yellow') {
          readings.warning++;
          component.status = component.status === 'CRITICAL' ? 'CRITICAL' : 'WARNING';
        } else if (d.status === 'red') {
          readings.critical++;
          component.status = 'CRITICAL';
        }
      }
    }
    if (component.readings.length > 0) {
      let healthSum = 0;
      for (const r of component.readings) {
        if (r.status === 'green') healthSum += 100;
        else if (r.status === 'yellow') healthSum += 60;
        else if (r.status === 'red') healthSum += 20;
      }
      component.health = Math.round(healthSum / component.readings.length);
    }
    components.push(component);
    collectComponentsRecursive(sub.childs, components, readings);
  }
}

export function findComponent(element: Element | null, componentId: string): Subsystem | null {
  if (!element) return null;
  let found: Subsystem | null = null;
  const search = (items: Subsystem[] | undefined): void => {
    if (!items || found) return;
    for (const item of items) {
      if (item.id === componentId) {
        found = item;
        return;
      }
      search(item.childs);
    }
  };
  search(element.subsystems);
  return found;
}

export function buildSystemReportPrompt(systemData: SystemData): string {
  return `You are an expert ground station systems analyst. Analyze the following system data and provide a comprehensive assessment.

SYSTEM: ${systemData.element ? systemData.element.name : 'Unknown'} (${systemData.element ? systemData.element.id : ''})
Overall Health: ${systemData.element ? systemData.element.operationalValue : 'N/A'}%
Status: ${systemData.element ? systemData.element.operationalStatus : 'UNKNOWN'}

SENSOR SUMMARY:
- Total sensors: ${systemData.readings.total}
- Healthy (green): ${systemData.readings.healthy}
- Warning (yellow): ${systemData.readings.warning}
- Critical (red): ${systemData.readings.critical}

ACTIVE ALERTS (${systemData.alerts.length}):
${systemData.alerts.map((a) => '- [' + a.type.toUpperCase() + '] ' + (a.subsystemName || a.subsystemId) + ': ' + a.message).join('\n') || 'None'}

FAILURE PREDICTIONS (${systemData.predictions.length}):
${
  systemData.predictions
    .slice(0, 5)
    .map(
      (p) =>
        '- ' + p.equipment + '/' + p.component + ': Predicted failure in ' + p.daysUntilFailure + ' days (' + p.confidence + '% confidence)',
    )
    .join('\n') || 'None'
}

LOW STOCK INVENTORY (${systemData.inventory.length}):
${systemData.inventory.map((i) => '- ' + i.name + ': ' + i.quantity + ' in stock (' + i.status + ')').join('\n') || 'None'}

COMPONENTS REQUIRING ATTENTION:
${
  systemData.components
    .filter((c) => c.status !== 'OPERATIONAL')
    .map((c) => '- ' + c.name + ': ' + c.status + ' (Health: ' + c.health + '%)')
    .join('\n') || 'All components operational'
}

Provide analysis in the following JSON format:
{
  "overallAssessment": "Brief overall system health assessment",
  "riskLevel": "LOW|MEDIUM|HIGH|CRITICAL",
  "criticalIssues": ["List of critical issues requiring immediate attention"],
  "warningIssues": ["List of warning issues to monitor"],
  "recommendations": ["Prioritized list of recommended actions"],
  "maintenanceNeeds": {
    "immediate": ["Actions needed within 24 hours"],
    "shortTerm": ["Actions needed within 1 week"],
    "scheduled": ["Actions for next maintenance window"]
  },
  "inventoryActions": ["Spare parts that should be ordered"],
  "predictedDowntime": "Estimated downtime risk in next 30 days",
  "confidenceLevel": "Assessment confidence percentage"
}`;
}

export function buildComponentPrompt(component: Subsystem): string {
  return `Analyze this ground station component and provide a detailed health assessment:

COMPONENT: ${component.label || component.id}
Description: ${component.description || 'N/A'}

SENSOR READINGS:
${
  (component.data || [])
    .map((d) => {
      const rangeStr = d.normalRange ? ' (Normal: ' + d.normalRange.low + ' - ' + d.normalRange.high + ')' : '';
      return '- ' + (d.label || d.id) + ': ' + d.value + ' [' + (d.status || 'unknown').toUpperCase() + ']' + rangeStr;
    })
    .join('\n') || 'No readings available'
}

Provide analysis in JSON format:
{
  "healthScore": 0-100,
  "status": "HEALTHY|DEGRADED|WARNING|CRITICAL",
  "assessment": "Brief assessment of component condition",
  "concerns": ["Specific concerns identified"],
  "recommendations": ["Recommended actions"],
  "predictedLifespan": "Estimated remaining useful life",
  "maintenanceAdvice": "Specific maintenance recommendations"
}`;
}

export function buildPredictionsPrompt(report: PredictionReport, inventory: PartSummary[]): string {
  return `Analyze these failure predictions and provide maintenance planning recommendations:

PREDICTIONS SUMMARY:
- Total predictions: ${report.predictions.length}
- Critical (< 30 days): ${report.summary.criticalWarnings}
- Warning (30-90 days): ${report.summary.warningAlerts}
- Average confidence: ${report.summary.averageConfidence}%

DETAILED PREDICTIONS:
${report.predictions
  .slice(0, 10)
  .map(
    (p) => `- ${p.equipment}/${p.component}:
    Health: ${p.currentHealth}%
    Predicted Failure: ${p.predictedFailure} (${p.daysUntilFailure} days)
    Confidence: ${p.confidence}%
    Trend: ${p.trend ? p.trend.direction : 'unknown'}`,
  )
  .join('\n')}

AVAILABLE SPARE PARTS:
${inventory.map((i) => '- ' + i.name + ' (' + i.equipment + '): ' + i.quantity + ' in stock').join('\n') || 'No inventory data'}

Provide analysis in JSON format:
{
  "analysisConfidence": 0-100,
  "overallRisk": "LOW|MEDIUM|HIGH|CRITICAL",
  "criticalComponents": [
    {
      "component": "Component name",
      "urgency": "IMMEDIATE|HIGH|MEDIUM|LOW",
      "reasoning": "Why this is critical",
      "recommendedAction": "Specific action",
      "sparePartStatus": "Available|Low Stock|Out of Stock|Unknown"
    }
  ],
  "maintenanceSchedule": [
    {
      "timeframe": "Immediate|This Week|This Month|Next Quarter",
      "actions": ["List of actions"],
      "resources": ["Required resources/parts"]
    }
  ],
  "procurementRecommendations": ["Parts to order with quantities"],
  "riskMitigation": ["Steps to reduce failure risk"],
  "confidenceNotes": "Factors affecting prediction confidence"
}`;
}

export function buildBatchAssessmentPrompt(components: AssessmentComponent[]): string {
  return `Provide brief AI assessments for each of these components. For each component, assess:
1. Current condition
2. Risk level
3. Key concern (if any)
4. Recommended action

COMPONENTS:
${components
  .map((c, i) => {
    const warningReadings = c.data ? c.data.filter((d) => d.status === 'yellow' || d.status === 'red') : [];
    return `${i + 1}. ${c.name} (${c.id})
   Status: ${c.status || 'OPERATIONAL'}
   Health: ${c.health || 100}%
   ${warningReadings.length > 0 ? 'Warnings: ' + warningReadings.map((r) => r.label + '=' + r.value).join(', ') : 'No warnings'}`;
  })
  .join('\n\n')}

Respond with a JSON array matching the component order:
[
  {
    "componentId": "id",
    "aiAssessment": "1-2 sentence assessment",
    "riskLevel": "LOW|MEDIUM|HIGH|CRITICAL",
    "confidence": 0-100,
    "action": "Recommended action or 'Continue monitoring'"
  }
]`;
}

export function getDefaultAnalysis(type: 'system_report'): SystemReportAnalysis;
export function getDefaultAnalysis(type: 'component'): ComponentAnalysis;
export function getDefaultAnalysis(type: 'predictions'): PredictionsAnalysis;
export function getDefaultAnalysis(type: AnalysisType): AnalysisResult;
export function getDefaultAnalysis(type: AnalysisType): AnalysisResult {
  const defaults: Record<AnalysisType, AnalysisResult> = {
    system_report: {
      overallAssessment: 'Unable to complete AI analysis. Manual review recommended.',
      riskLevel: 'UNKNOWN',
      criticalIssues: [],
      warningIssues: [],
      recommendations: ['Complete manual system review'],
      maintenanceNeeds: { immediate: [], shortTerm: [], scheduled: [] },
      inventoryActions: [],
      predictedDowntime: 'Unable to assess',
      confidenceLevel: 0,
    },
    component: {
      healthScore: 0,
      status: 'UNKNOWN',
      assessment: 'Unable to complete AI analysis.',
      concerns: [],
      recommendations: ['Manual inspection required'],
      predictedLifespan: 'Unknown',
      maintenanceAdvice: 'Follow standard maintenance procedures',
    },
    predictions: {
      analysisConfidence: 0,
      overallRisk: 'UNKNOWN',
      criticalComponents: [],
      maintenanceSchedule: [],
      procurementRecommendations: [],
      riskMitigation: ['Review prediction data manually'],
      confidenceNotes: 'AI analysis unavailable',
    },
  };
  return defaults[type];
}

/** The JSON object in a string (or the object itself) tagged with the type and time; the default analysis when it does not parse. */
export function parseAnalysisResponse(response: unknown, type: AnalysisType, nowMs: number): AnalysisResult {
  let parsed: Record<string, unknown> | null = null;
  if (response && typeof response === 'object' && !Array.isArray(response)) {
    parsed = response as Record<string, unknown>;
  } else if (typeof response === 'string') {
    const jsonMatch = response.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        parsed = JSON.parse(jsonMatch[0]) as Record<string, unknown>;
      } catch {
        parsed = null;
      }
    }
  }
  if (!parsed) return getDefaultAnalysis(type);
  parsed._analysisType = type;
  parsed._generatedAt = new Date(nowMs).toISOString();
  return parsed as AnalysisResult;
}

export function generateDefaultAssessments(components: AssessmentComponent[]): ComponentAssessment[] {
  return components.map((c) => {
    let riskLevel = 'LOW';
    let assessment = 'Operating within normal parameters.';
    if (c.status === 'WARNING' || (c.health as number) < 70) {
      riskLevel = 'MEDIUM';
      assessment = 'Some readings outside normal range. Monitor closely.';
    } else if (c.status === 'CRITICAL' || (c.health as number) < 50) {
      riskLevel = 'HIGH';
      assessment = 'Critical readings detected. Immediate attention recommended.';
    }
    return {
      componentId: c.id,
      aiAssessment: assessment,
      riskLevel,
      confidence: 70,
      action: riskLevel === 'LOW' ? 'Continue monitoring' : 'Review component status',
    };
  });
}

/** The JSON array in the response (or an array given directly) mapped back onto the components by position. */
export function parseBatchAssessments(response: unknown, components: AssessmentComponent[]): ComponentAssessment[] {
  let assessments: Partial<ComponentAssessment>[] | null = null;
  if (Array.isArray(response)) assessments = response as Partial<ComponentAssessment>[];
  else if (response && typeof response === 'object') {
    // A json_object response wraps the array in its first array-valued field.
    const firstArray = Object.values(response as Record<string, unknown>).find((v) => Array.isArray(v));
    if (firstArray) assessments = firstArray as Partial<ComponentAssessment>[];
  } else if (typeof response === 'string') {
    const jsonMatch = response.match(/\[[\s\S]*\]/);
    if (jsonMatch) {
      try {
        assessments = JSON.parse(jsonMatch[0]) as Partial<ComponentAssessment>[];
      } catch {
        assessments = null;
      }
    }
  }
  if (!assessments) return generateDefaultAssessments(components);
  const list = assessments;
  return components.map((c, i) => {
    const assessment = list[i] || {};
    return {
      componentId: c.id,
      aiAssessment: assessment.aiAssessment || 'Assessment unavailable',
      riskLevel: assessment.riskLevel || 'UNKNOWN',
      confidence: assessment.confidence || 0,
      action: assessment.action || 'Continue monitoring',
    };
  });
}

export function createAiAnalysisService(deps: AiAnalysisDeps): AiAnalysisService {
  const cache: Record<string, { data: AnalysisResult; timestamp: number }> = {};

  function cached<T extends AnalysisResult>(key: string): T | null {
    const entry = cache[key];
    if (!entry) return null;
    return deps.nowMs() - entry.timestamp < AI_ANALYSIS_CACHE_VALIDITY_MS ? (entry.data as T) : null;
  }

  function store<T extends AnalysisResult>(key: string, data: T): T {
    cache[key] = { data, timestamp: deps.nowMs() };
    return data;
  }

  async function collectSystemData(element: Element | null): Promise<SystemData> {
    const data: SystemData = {
      element: null,
      components: [],
      alerts: [],
      predictions: [],
      inventory: [],
      readings: { total: 0, healthy: 0, warning: 0, critical: 0 },
    };
    if (element) {
      data.element = {
        id: element.id,
        name: element.callsign,
        operationalValue: element.operationalValue,
        operationalStatus: element.operationalStatus,
      };
      collectComponentsRecursive(element.subsystems, data.components, data.readings);
    }
    data.alerts = deps.getActiveAlerts ? deps.getActiveAlerts() : [];
    if (deps.predictionEngine) {
      // Awaited (the original concatenated the two Promises and always caught a TypeError).
      data.predictions = (await deps.predictionEngine.getCriticalPredictions()).concat(
        await deps.predictionEngine.getWarningPredictions(),
      );
    }
    if (deps.inventory) data.inventory = deps.inventory.getLowStockParts();
    return data;
  }

  async function call(prompt: string): Promise<unknown> {
    return deps.transport.call('analyze', buildAiMessages(AI_ANALYSIS_SYSTEM_PROMPT, prompt));
  }

  return {
    collectSystemData,
    async analyzeSystemReport(element) {
      const cacheKey = 'system_report_' + (element ? element.id : 'unknown');
      const hit = cached<SystemReportAnalysis>(cacheKey);
      if (hit) return hit;
      const systemData = await collectSystemData(element);
      const response = await call(buildSystemReportPrompt(systemData));
      return store(cacheKey, parseAnalysisResponse(response, 'system_report', deps.nowMs()) as SystemReportAnalysis);
    },
    async analyzeComponent(element, componentId) {
      const cacheKey = 'component_' + componentId;
      const hit = cached<ComponentAnalysis>(cacheKey);
      if (hit) return hit;
      const componentData = findComponent(element, componentId);
      if (!componentData) throw new Error('Component not found: ' + componentId);
      const response = await call(buildComponentPrompt(componentData));
      return store(cacheKey, parseAnalysisResponse(response, 'component', deps.nowMs()) as ComponentAnalysis);
    },
    async analyzePredictions() {
      const cacheKey = 'predictions_analysis';
      const hit = cached<PredictionsAnalysis>(cacheKey);
      if (hit) return hit;
      if (!deps.predictionEngine) throw new Error('PredictionEngine not available');
      const report = await deps.predictionEngine.generateFullReport();
      const inventoryData = deps.inventory ? deps.inventory.getAllParts() : [];
      const response = await call(buildPredictionsPrompt(report, inventoryData));
      return store(cacheKey, parseAnalysisResponse(response, 'predictions', deps.nowMs()) as PredictionsAnalysis);
    },
    async assessComponentsForReport(components) {
      try {
        const response = await call(buildBatchAssessmentPrompt(components));
        return parseBatchAssessments(response, components);
      } catch {
        return generateDefaultAssessments(components);
      }
    },
    clearCache() {
      for (const key of Object.keys(cache)) delete cache[key];
    },
  };
}
